/**
 * Returning-visitor policy for /apply (2026-07-22 review, item 12).
 *
 * Pure — no I/O — so the wizard can compute which returning screen to show
 * (received / choice / newOnly) without a round trip. The `audition.resubmit`
 * mutation enforces the once-only latch and 60-day window itself, in its CAS
 * WHERE clause; it deliberately does NOT re-check the 21-day quiet period
 * (the latch already blocks a second resubmit, and a client only ever offers
 * the button in the `choice` state). So this function governs what the user
 * SEES; the server WHERE is the authority on what actually commits. Keep the
 * `choice` preconditions here in step with that WHERE when either changes.
 *
 * Two windows, deliberately anchored to different timestamps:
 *  - The quiet period anchors to the MOST RECENT submission, so resubmitting
 *    does not immediately re-offer the same choice.
 *  - The resubmit cap anchors to the ORIGINAL submission, so a resubmit
 *    cannot buy another window. An application has one lifetime.
 */
import type { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc'
import type { AuditionStage } from './state-machine.js'

/** How long after a submission we just say "we got it, sit tight". */
export const QUIET_PERIOD_DAYS = 21
/**
 * How long an application stays re-submittable. Past this, the answers are
 * assumed stale enough that a fresh application is the honest ask.
 */
export const RESUBMIT_WINDOW_DAYS = 60

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Whether an unfinished (`wizard_in_progress`) draft is too old to resume
 * (LG-1046). Same window as the resubmit cap — one idea, one value — so a
 * draft dies at about the moment its 60-day resume link does.
 *
 * Anchored to `createdAt`, not `updatedAt`, for the same reason the resubmit
 * cap anchors to the original submission: an application has one lifetime,
 * and activity cannot buy another. `updatedAt` would not even measure
 * activity honestly — `audition.start`'s reuse write bumps it just for
 * retyping an email, and so does soft-deleting the row.
 */
export function isDraftExpired({
  createdAt,
  now,
}: {
  createdAt: Date
  now: Date
}): boolean {
  return now.getTime() - createdAt.getTime() >= RESUBMIT_WINDOW_DAYS * DAY_MS
}

/**
 * The tRPC code `loadSubmission` refuses an expired draft with, and the code
 * `isDraftExpiredError` reads. One constant for both, so the throw and the
 * reading cannot drift apart, and checked against tRPC's codes so a misspelled
 * one fails to compile. (A type-only import: nothing server-side reaches the
 * client bundle.)
 *
 * The name is the meaning on four procedures only, not a code unique to
 * expiry: the value is the generic `PRECONDITION_FAILED`, and `createCheckout`
 * already refuses an unlinked account with it. Read it as "expired" only from
 * `get`, `saveStep`, `saveLeetcode` and `linkToAccount`.
 */
export const DRAFT_EXPIRED_ERROR_CODE =
  'PRECONDITION_FAILED' satisfies TRPC_ERROR_CODE_KEY

/**
 * Whether a tRPC error from one of the wizard's submission-addressed
 * procedures (`get`, `saveStep`, `saveLeetcode`, `linkToAccount`) is the server refusing a
 * draft past its window — `loadSubmission`'s `isDraftExpired` check.
 *
 * The code alone carries that meaning, and `PRECONDITION_FAILED` is the
 * codebase's generic wrong-state code, so the reading holds only while the
 * window refusal is the one wrong-state refusal those procedures raise. On the
 * server that warning sits on `loadSubmission`, the token lookup all four
 * call, not beside the refusals in their bodies. On a match the client
 * restarts the applicant rather than offering a retry.
 */
export function isDraftExpiredError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return false
  }
  const { data } = error
  return (
    typeof data === 'object' &&
    data !== null &&
    'code' in data &&
    data.code === DRAFT_EXPIRED_ERROR_CODE
  )
}

export type ReturningState =
  /** Nothing to do — we have it and it is recent (or an admin owns it now). */
  | 'received'
  /** Offer both: resubmit this application, or start a new one. */
  | 'choice'
  /** Resubmit is spent or expired — a new application is the only path. */
  | 'newOnly'

export function returningState({
  stage,
  submittedAt,
  resubmittedAt,
  createdAt,
  now,
}: {
  stage: AuditionStage
  submittedAt: Date | null
  resubmittedAt: Date | null
  /** Fallback anchor for rows that predate the submittedAt column. */
  createdAt: Date
  now: Date
}): ReturningState {
  // Anything past audition_completed means a human or a payment is already
  // driving this application. Restarting would orphan that work.
  if (stage !== 'audition_completed') return 'received'

  const submitted = submittedAt ?? createdAt
  const lastSubmitted = resubmittedAt ?? submitted

  if (now.getTime() - lastSubmitted.getTime() < QUIET_PERIOD_DAYS * DAY_MS)
    return 'received'

  if (resubmittedAt) return 'newOnly'

  if (now.getTime() - submitted.getTime() >= RESUBMIT_WINDOW_DAYS * DAY_MS)
    return 'newOnly'

  return 'choice'
}
